// Agent Types
export enum AIAgentType {
  ACCOUNT_BRIEF = "account_brief",
  BUSINESS_GOAL = "business_goal",
  BUSINESS_GOAL_BRIEF = "business_goal_brief",
  TECHNOLOGY = "technology",
  TECHNOLOGY_BRIEF = "technology_brief",
  PRODUCT_FEEDBACK = "product_feedback",
  PRODUCT_FEEDBACK_BRIEF = "product_feedback_brief",
  ORGANIZATION_FEEDBACK_THEME = "organization_feedback_theme",
  ORGANIZATION_CONTEXT = "organization_context",
  ACTION_ITEM = "action_item",
  ACTION_ITEM_BRIEF = "action_item_brief",
  SIGNAL = "signal",
  SIGNAL_BRIEF = "signal_brief",
  ACTIVITY_SENTIMENT = "activity_sentiment",
  PREP_TALKING_POINTS = "prep_talking_points",
  PREP_SUGGESTED_GOAL = "prep_suggested_goal",
  // Contact-scoped. The bg-agent resolves this exact string as its canonical
  // agent_type (`CONTACT_KEY_DRIVERS_CLAIM_TYPE` in contact_discovery_service),
  // and toggles default OFF — so without this value the SuperAdmin sheet renders
  // no row and the agent can never be enabled for any organization.
  CONTACT_KEY_DRIVERS = "contact_key_drivers",
  // Activity-scoped, silent. The bg-agent reflects this string from the DB enum
  // as its canonical agent_type and as the analysis_type of its claim (SKR-782).
  RECAP_EMAIL = "recap_email",
  // Activity-scoped, silent. Checks off the linked prep's talking points that
  // were discussed on the call, from the transcript (SKR-859). Same routing
  // contract as RECAP_EMAIL: the string is the DB enum value on both sides.
  TALKING_POINT_COVERAGE = "talking_point_coverage",
  // Activity-scoped, silent. Writes the AI Call Summary on the Review Call
  // Output page (SKR-909); the bg-agent reflects this string from the DB enum.
  CALL_SUMMARY = "call_summary",
}

// Agent Workflow Status
export enum AIAgentWorkflowStatus {
  PENDING = "pending",
  RUNNING = "running",
  SLEEPING = "sleeping",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELED = "canceled",
}

/**
 * Non-terminal workflow statuses. Single source of truth for "can it be canceled?":
 * used by the service guard (B2) and the UI gating (B7/B8). Forward-compatible:
 * any new status (e.g. `canceled` from SKR-684) is treated as terminal without
 * touching this set.
 */
export const NON_TERMINAL_WORKFLOW_STATUSES: readonly AIAgentWorkflowStatus[] =
  [
    AIAgentWorkflowStatus.PENDING,
    AIAgentWorkflowStatus.RUNNING,
    AIAgentWorkflowStatus.SLEEPING,
  ];

// Agent Batch Job Types
export enum AIAgentBatchJobType {
  GENERATE_ANALYSIS = "generate_analysis",
  GENERATE_BRIEF = "generate_brief",
  GENERATE_FEEDBACK_THEME_CLASSIFICATION = "generate_feedback_theme_classification",
  GENERATE_FEEDBACK_THEME_METADATA = "generate_feedback_theme_metadata",
  GENERATE_ORGANIZATION_CONTEXT_PRODUCT_RESEARCH = "generate_organization_context_product_research",
  GENERATE_ORGANIZATION_CONTEXT_MARKET_RESEARCH = "generate_organization_context_market_research",
  GENERATE_ORGANIZATION_CONTEXT_SYNTHESIS = "generate_organization_context_synthesis",
  GENERATE_ACTIVITY_SENTIMENT = "generate_activity_sentiment",
  INTRA_BATCH_DUPLICATE_CHECK = "intra_batch_duplicate_check",
  EXISTING_DATA_DUPLICATE_CHECK = "existing_data_duplicate_check",
  GENERATE_CONTACT_KEY_DRIVERS = "generate_contact_key_drivers",
  GENERATE_TALKING_POINTS = "generate_talking_points",
  GENERATE_SUGGESTED_GOAL = "generate_suggested_goal",
  DISTIL_FEEDBACK = "distil_feedback",
}

// Agent Batch Job Status
export enum AIAgentBatchJobStatus {
  SUBMITTED = "submitted",
  IN_PROGRESS = "in_progress",
  ENDED = "ended",
  CANCELING = "canceling",
}

// Agent Batch Individual Request Status
export enum AIAgentBatchRequestStatus {
  PENDING = "pending",
  SUBMITTED = "submitted",
  SUCCEEDED = "succeeded",
  ERRORED = "errored",
  CANCELED = "canceled",
  EXPIRED = "expired",
}

// AI Agent Claim Status and Types
export enum AIAgentClaimStatus {
  IDLE = "idle",
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
  CANCELED = "canceled",
}

export enum AIAgentClaimAnalysisType {
  BUSINESS_GOAL = "business_goal",
  TECHNOLOGY = "technology",
  PRODUCT_FEEDBACK = "product_feedback",
  ACTION_ITEM = "action_item",
  SIGNAL = "signal",
  ACTIVITY_SENTIMENT = "activity_sentiment",
  RECAP_EMAIL = "recap_email",
  TALKING_POINT_COVERAGE = "talking_point_coverage",
  CALL_SUMMARY = "call_summary",
}

/** Why analysis discovery has not claimed an activity yet. */
export enum AIAgentAnalysisPendingReason {
  ARCHIVED = "archived",
  ANALYSIS_DISABLED = "analysis_disabled",
  AWAITING_RECORDING = "awaiting_recording",
  QUEUED = "queued",
}

export enum AIAgentClaimBriefType {
  ACCOUNT = "account",
  BUSINESS_GOAL = "business_goal",
  TECHNOLOGY = "technology",
  PRODUCT_FEEDBACK = "product_feedback",
  ACTION_ITEM = "action_item",
  SIGNAL = "signal",
}

export enum AIAgentOrganizationClaimType {
  ORGANIZATION_FEEDBACK_THEME = "organization_feedback_theme",
  ORGANIZATION_CONTEXT = "organization_context",
}

export enum AIAgentPrepClaimType {
  SUGGESTED_GOAL = "suggested_goal",
}

export enum AIAgentContactClaimType {
  CONTACT_KEY_DRIVERS = "contact_key_drivers",
}

export enum AgentFeedback {
  POSITIVE = "POSITIVE",
  NEGATIVE = "NEGATIVE",
}

export enum AgentFeedbackLabel {
  POSITIVE = "Positive",
  NEGATIVE = "Negative",
}

// Agent Workflow Command Type
export enum AIAgentWorkflowCommandType {
  RERUN = "rerun",
  CANCEL = "cancel",
}

// Agent Workflow Command Status
export enum AIAgentWorkflowCommandStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  DONE = "done",
  FAILED = "failed",
}
